import { Check, Play, RotateCcw, SquareChevronRight } from "lucide-react";
import {
  type FormEvent,
  type KeyboardEvent,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";

import { Badge } from "@/ui/badge";
import { Button } from "@/ui/button";
import { cn } from "@/utils/tailwind";

import { InlineText } from "./inline-text";
import { codeFontStyle } from "./source-browser";
import type { TerminalReplayData, TerminalStep } from "./types";

type TerminalReplayProps = TerminalReplayData;

export function TerminalReplay({ context, steps, title }: TerminalReplayProps) {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isTerminalComposing, setIsTerminalComposing] = useState(false);
  const [isTerminalFocused, setIsTerminalFocused] = useState(false);
  const [terminalInputScrollLeft, setTerminalInputScrollLeft] = useState(0);
  const [terminalSelectionEnd, setTerminalSelectionEnd] = useState(0);
  const [terminalSelectionStart, setTerminalSelectionStart] = useState(0);
  const [typedCommand, setTypedCommand] = useState("");
  const [terminalError, setTerminalError] = useState("");
  const terminalErrorId = useId();
  const terminalInstructionId = useId();
  const restartButtonRef = useRef<HTMLButtonElement>(null);
  const shouldAutoScrollTranscriptRef = useRef(true);
  const terminalInputRef = useRef<HTMLInputElement>(null);
  const terminalTranscriptRef = useRef<HTMLDivElement>(null);

  const currentStep = steps[currentStepIndex];
  const completedSteps = steps.slice(0, currentStepIndex);
  const completedExplanations = completedSteps.flatMap((step, index) => {
    return step.explanation
      ? [{ stepIndex: index, text: step.explanation }]
      : [];
  });
  const isComplete = currentStepIndex >= steps.length;
  const progressLabel = isComplete
    ? "complete"
    : `step ${currentStepIndex + 1}/${steps.length}`;
  const completionSuffix = currentStep
    ? getCommandCompletionSuffix(typedCommand, currentStep.command)
    : "";
  const terminalSelectionOffset = Math.min(
    Math.max(terminalSelectionStart, 0),
    typedCommand.length,
  );
  const shouldShowTerminalCursor =
    !isTerminalComposing && terminalSelectionStart === terminalSelectionEnd;

  useEffect(() => {
    if (isComplete) {
      restartButtonRef.current?.focus();
    }
  }, [isComplete]);

  useEffect(() => {
    const terminalTranscript = terminalTranscriptRef.current;
    if (!terminalTranscript) {
      return;
    }

    if (shouldAutoScrollTranscriptRef.current) {
      terminalTranscript.scrollTop = terminalTranscript.scrollHeight;
    }
  }, [currentStepIndex]);

  function updateTranscriptAutoScroll(transcript: HTMLDivElement) {
    const bottomDistance =
      transcript.scrollHeight - transcript.scrollTop - transcript.clientHeight;
    shouldAutoScrollTranscriptRef.current = bottomDistance <= 32;
  }

  function updateTerminalSelection(input: HTMLInputElement) {
    setTerminalSelectionStart(input.selectionStart ?? input.value.length);
    setTerminalSelectionEnd(input.selectionEnd ?? input.value.length);
  }

  function submitTerminalCommand(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!currentStep) {
      return;
    }

    if (typedCommand.trim() !== currentStep.command) {
      setTerminalError(`This replay expects: ${currentStep.command}`);
      return;
    }

    setCurrentStepIndex(currentStepIndex + 1);
    setTerminalInputScrollLeft(0);
    setTerminalSelectionEnd(0);
    setTerminalSelectionStart(0);
    setTypedCommand("");
    setTerminalError("");
  }

  function completeTerminalCommand(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key !== "Tab" || event.shiftKey || !currentStep) {
      return;
    }

    if (
      typedCommand === currentStep.command ||
      !currentStep.command.startsWith(typedCommand)
    ) {
      return;
    }

    event.preventDefault();
    setTypedCommand(currentStep.command);
    setTerminalSelectionEnd(currentStep.command.length);
    setTerminalSelectionStart(currentStep.command.length);
    setTerminalError("");
  }

  function restartTerminalReplay() {
    shouldAutoScrollTranscriptRef.current = true;
    setCurrentStepIndex(0);
    setIsTerminalComposing(false);
    setIsTerminalFocused(false);
    setTerminalInputScrollLeft(0);
    setTerminalSelectionEnd(0);
    setTerminalSelectionStart(0);
    setTypedCommand("");
    setTerminalError("");

    requestAnimationFrame(() => {
      terminalInputRef.current?.focus();
    });
  }

  return (
    <div className="bg-card overflow-hidden rounded-md border shadow-sm">
      <div className="bg-muted/30 flex min-h-14 flex-wrap items-start justify-between gap-3 border-b px-4 py-3">
        <div className="flex min-w-0 items-start gap-2">
          <SquareChevronRight className="text-muted-foreground mt-0.5 size-4.5 shrink-0" />
          <div className="min-w-0">
            <p className="min-w-0 truncate font-medium">
              <InlineText text={title} />
            </p>
            <p className="text-muted-foreground text-sm leading-5">
              <InlineText text={context} />
            </p>
          </div>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-2">
          <Badge
            className="font-mono"
            variant={isComplete ? "secondary" : "outline"}
          >
            {progressLabel}
          </Badge>
          <TerminalProgress currentStepIndex={currentStepIndex} steps={steps} />
        </div>
      </div>

      <form onSubmit={submitTerminalCommand}>
        <div className="overflow-hidden bg-zinc-950">
          <div className="flex h-9 items-center gap-1.5 border-b border-zinc-800 bg-zinc-900 px-4">
            <span className="size-2.5 rounded-full bg-red-400/80" />
            <span className="size-2.5 rounded-full bg-amber-300/80" />
            <span className="size-2.5 rounded-full bg-emerald-400/80" />
            <span className="ml-2 font-mono text-xs text-zinc-500">
              interactive replay
            </span>
          </div>

          <div
            ref={terminalTranscriptRef}
            aria-label="Interactive terminal transcript"
            className="max-h-[min(28rem,55dvh)] min-h-48 cursor-text space-y-1 overflow-y-auto p-4 font-mono text-[13px] leading-5 text-zinc-100"
            role="region"
            style={codeFontStyle}
            onClick={(event) => {
              if (
                !currentStep ||
                event.target === terminalInputRef.current ||
                window.getSelection()?.toString()
              ) {
                return;
              }

              terminalInputRef.current?.focus();
            }}
            onScroll={(event) => {
              updateTranscriptAutoScroll(event.currentTarget);
            }}
          >
            {completedSteps.map((step, index) => (
              <TerminalEntry key={`${index}-${step.command}`} step={step} />
            ))}

            {currentStep && (
              <label className="group/command grid grid-cols-[auto_minmax(0,1fr)] items-center gap-2">
                <span className="text-emerald-300/80 transition-colors group-focus-within/command:text-emerald-300">
                  $
                </span>
                <div className="relative min-w-0">
                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 flex min-w-0 items-center overflow-hidden whitespace-pre"
                  >
                    <span
                      className="flex min-w-max items-center"
                      style={{
                        transform: `translateX(-${terminalInputScrollLeft}px)`,
                      }}
                    >
                      <span className="invisible">
                        {typedCommand.slice(0, terminalSelectionOffset)}
                      </span>
                      {shouldShowTerminalCursor && (
                        <span className="relative h-[1.15em] w-0 shrink-0">
                          <span
                            className={cn(
                              "absolute inset-y-0 left-0 w-[1ch] rounded-[1px]",
                              isTerminalFocused
                                ? "bg-zinc-400/70"
                                : "bg-zinc-600/70",
                            )}
                          />
                        </span>
                      )}
                      <span className="invisible">
                        {typedCommand.slice(terminalSelectionOffset)}
                      </span>
                      <span className="text-zinc-500">{completionSuffix}</span>
                    </span>
                  </div>
                  <input
                    ref={terminalInputRef}
                    aria-invalid={Boolean(terminalError)}
                    aria-label="Terminal command"
                    autoCapitalize="none"
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck={false}
                    value={typedCommand}
                    aria-describedby={
                      terminalError
                        ? `${terminalInstructionId} ${terminalErrorId}`
                        : terminalInstructionId
                    }
                    className={cn(
                      "relative z-10 w-full bg-transparent text-zinc-100 outline-none selection:bg-zinc-700",
                      isTerminalComposing
                        ? "caret-zinc-100"
                        : "caret-transparent",
                    )}
                    onCompositionStart={() => setIsTerminalComposing(true)}
                    onKeyDown={completeTerminalCommand}
                    onBlur={(event) => {
                      setIsTerminalFocused(false);
                      updateTerminalSelection(event.currentTarget);
                    }}
                    onChange={(event) => {
                      setTypedCommand(event.currentTarget.value);
                      updateTerminalSelection(event.currentTarget);
                      setTerminalError("");
                    }}
                    onCompositionEnd={(event) => {
                      setIsTerminalComposing(false);
                      updateTerminalSelection(event.currentTarget);
                    }}
                    onFocus={(event) => {
                      setIsTerminalFocused(true);
                      updateTerminalSelection(event.currentTarget);
                    }}
                    onScroll={(event) => {
                      setTerminalInputScrollLeft(
                        event.currentTarget.scrollLeft,
                      );
                    }}
                    onSelect={(event) => {
                      updateTerminalSelection(event.currentTarget);
                    }}
                  />
                </div>
              </label>
            )}
          </div>
        </div>

        <div className="bg-muted/20 grid gap-3 border-t px-4 py-3">
          {completedExplanations.length > 0 && (
            <div className="grid gap-2">
              {completedExplanations.map((explanation) => (
                <p
                  key={explanation.stepIndex}
                  className="text-muted-foreground text-sm leading-6"
                >
                  <InlineText text={explanation.text} />
                </p>
              ))}
            </div>
          )}

          {currentStep ? (
            <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center">
              <div className="grid gap-1">
                <p
                  className="text-muted-foreground text-xs"
                  id={terminalInstructionId}
                >
                  {isTerminalFocused
                    ? "Tab completes the command. Enter runs it."
                    : "Select the terminal to begin, or choose Auto-fill."}
                </p>
                {terminalError && (
                  <p
                    className="text-sm text-balance text-amber-700 dark:text-amber-300"
                    id={terminalErrorId}
                    role="alert"
                  >
                    {terminalError}
                  </p>
                )}
              </div>

              <div className="flex justify-end gap-2">
                <Button
                  aria-label="Auto-fill expected command"
                  size="sm"
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setTypedCommand(currentStep.command);
                    setTerminalSelectionEnd(currentStep.command.length);
                    setTerminalSelectionStart(currentStep.command.length);
                    setTerminalError("");
                    requestAnimationFrame(() => {
                      terminalInputRef.current?.focus();
                      terminalInputRef.current?.setSelectionRange(
                        currentStep.command.length,
                        currentStep.command.length,
                      );
                    });
                  }}
                >
                  Auto-fill
                </Button>
                <Button size="sm" type="submit">
                  <Play className="size-3.5" />
                  Run
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-success flex items-center gap-2 text-sm">
                <Check className="size-4" />
                Terminal replay complete.
              </p>
              <Button
                ref={restartButtonRef}
                size="sm"
                type="button"
                variant="outline"
                onClick={restartTerminalReplay}
              >
                <RotateCcw className="size-3.5" />
                Restart
              </Button>
            </div>
          )}

          <p aria-live="polite" className="sr-only">
            {getTerminalProgressAnnouncement({
              completedStep: completedSteps.at(-1),
              currentStepIndex,
              isComplete,
            })}
          </p>
        </div>
      </form>
    </div>
  );
}

function getTerminalProgressAnnouncement({
  completedStep,
  currentStepIndex,
  isComplete,
}: {
  completedStep: TerminalStep | undefined;
  currentStepIndex: number;
  isComplete: boolean;
}) {
  const progress = isComplete
    ? "Terminal transcript complete."
    : currentStepIndex > 0
      ? `Terminal step ${currentStepIndex} complete.`
      : "";
  const explanation = completedStep?.explanation?.replaceAll("`", "") ?? "";

  return [progress, explanation].filter(Boolean).join(" ");
}

function getCommandCompletionSuffix(
  typedCommand: string,
  expectedCommand: string,
) {
  if (!expectedCommand.startsWith(typedCommand)) {
    return "";
  }

  return expectedCommand.slice(typedCommand.length);
}

interface TerminalProgressProps {
  currentStepIndex: number;
  steps: readonly TerminalStep[];
}

function TerminalProgress({ currentStepIndex, steps }: TerminalProgressProps) {
  const completedStepCount = Math.min(currentStepIndex, steps.length);

  return (
    <div
      aria-label={`${completedStepCount} of ${steps.length} terminal steps complete`}
      aria-valuemax={steps.length}
      aria-valuemin={0}
      aria-valuenow={completedStepCount}
      className="flex items-center gap-1"
      role="progressbar"
    >
      {steps.map((step, index) => (
        <span
          key={`${index}-${step.command}`}
          className={cn(
            "h-1.5 w-6 rounded-full",
            index < currentStepIndex && "bg-success",
            index === currentStepIndex && "bg-foreground/70",
            index > currentStepIndex && "bg-muted-foreground/25",
          )}
        />
      ))}
    </div>
  );
}

interface TerminalEntryProps {
  step: TerminalStep;
}

function TerminalEntry({ step }: TerminalEntryProps) {
  return (
    <div className="grid">
      <p className="flex min-w-0 gap-2">
        <span className="text-emerald-300">$</span>
        <span className="break-all">{step.command}</span>
      </p>
      {step.output.map((line, index) => (
        <p
          key={`${index}-${line}`}
          className="whitespace-pre-wrap text-zinc-300"
        >
          {line}
        </p>
      ))}
    </div>
  );
}
